// Structure and copy ported from files/snapdraw-design-kit/code/src/components/snapdraw/aia-sheet.tsx
// (Lovable reference implementation), backlog M2 (2026-08-24) — but wired to
// our REAL stored G702Item[] and computeG702Totals()/lineCalc() math
// (@/features/g702/g702Math) instead of the kit's demo-only per-doc-id
// number generator (it has no backend, so it reverse-engineers plausible
// totals from just an amount; we already have the real schedule of values).
//
// This is the product's differentiator: a pay application must print as the
// form a GC expects, not as a generic invoice. Lines are numbered 1-9 as on
// the paper form, retainage is broken out completed vs. stored, and the
// certification and notary blocks are present because most GCs will not pay
// a sub's application without them. Fidelity target: MANUAL-TEST-PLAN §D2.
import type { ReactNode } from "react";

import { computeG702Totals, lineCalc, money } from "@/features/g702/g702Math";
import { useT } from "@/lib/i18n";
import { useResolvedLogoUrl } from "@/lib/useResolvedLogoUrl";
import { formatDueDate } from "@/lib/docStatus";
import { cn } from "@/lib/utils";
import type { G702Application } from "@/lib/types";

/** "Ridgeline Business Park – App #4" -> 4. */
function applicationNumber(doc: G702Application): number {
  const match = /#\s*(\d+)/.exec(doc.projectName ?? "");
  return match ? Number(match[1]) : 1;
}

export function AiaSheet({
  doc,
  resolvedLogoUrl,
}: {
  doc: G702Application;
  /**
   * Pre-resolved signed logo URL (backlog PRINT-26) — set only by the
   * print-only route (PrintDocumentPage), which has no Supabase session to
   * resolve this itself; see useResolvedLogoUrl's own comment. Omitted
   * here, behavior is unchanged.
   */
  resolvedLogoUrl?: string | null;
}) {
  const t = useT();
  const logoUrl = useResolvedLogoUrl(doc.bizLogoPath, resolvedLogoUrl);
  const appNo = applicationNumber(doc);
  const retainagePct = parseFloat(doc.retainagePct) || 0;

  const totals = computeG702Totals(doc.items, {
    contractSum: parseFloat(doc.contractSum) || 0,
    changeOrders: parseFloat(doc.changeOrders) || 0,
    retainagePct,
    previousCerts: parseFloat(doc.previousCerts) || 0,
  });

  const rows = doc.items.map((item) => ({ item, calc: lineCalc(item, retainagePct) }));
  const percentComplete = totals.contractSumToDate > 0 ? (totals.totalCompletedToDate / totals.contractSumToDate) * 100 : 0;

  return (
    <>
      <article className="sd-sheet sd-sheet-page1 sd-card mt-5 p-6 lg:p-10">
        <header className="flex flex-wrap items-start justify-between gap-6 border-b border-border pb-6">
          <div className="min-w-0">
            <div className="sd-aia-form-label sd-label">{t("aia.formTitle")}</div>
            <h1 className="sd-aia-title mt-2 font-display text-2xl uppercase tracking-wide lg:text-4xl">{t("aia.docTitle")}</h1>
            <div className="sd-numeric mt-3 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              {doc.docId.toUpperCase()} · {t("docs.view.rev", { rev: doc.revision })} · {t("aia.appNo", { no: appNo })}
            </div>
          </div>
          <div className="min-w-0 text-right">
            {logoUrl ? <img src={logoUrl} alt="" className="sd-doc-logo" /> : null}
            <div className="sd-label break-words">{doc.bizName}</div>
            <div className="mt-2 text-sm text-muted-foreground">{doc.bizPhone}</div>
            <div className="break-words text-sm text-muted-foreground">{doc.bizEmail}</div>
          </div>
        </header>

        {/* Form parties + period */}
        <div className="sd-block sd-g702-parties grid gap-6 border-b border-border py-6 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("aia.toOwner")}>
            <div className="break-words text-base font-semibold">{doc.clientName}</div>
            <div className="break-words text-sm text-muted-foreground">{doc.clientAddress}</div>
          </Field>
          <Field label={t("aia.fromContractor")}>
            <div className="text-base font-semibold">{doc.bizName}</div>
          </Field>
          <Field label={t("aia.project")}>
            <div className="text-base font-semibold">{doc.projectName || doc.docId}</div>
            <div className="sd-numeric text-sm text-muted-foreground">
              {t("aia.viaArchitect")}: {t("aia.notApplicable")}
            </div>
          </Field>
          <Field label={t("aia.periodTo")}>
            <div className="sd-numeric text-base font-semibold">
              {doc.status === "draft" ? t("aia.pendingDate") : formatDueDate(doc.periodTo)}
            </div>
            <div className="sd-numeric text-sm text-muted-foreground">
              {t("aia.contractFor")}: {t(`docs.due.terms.${doc.termId}`)}
            </div>
          </Field>
        </div>

        {/* Lines 1-9 */}
        <div className="sd-block py-6">
          <div className="sd-label">{t("aia.summaryTitle")}</div>
          <dl className="mt-4 divide-y divide-border">
            <AiaLine n="1" label={t("aia.l1")} value={totals.contractSum} />
            <AiaLine n="2" label={t("aia.l2")} value={totals.changeOrders} signed />
            <AiaLine n="3" label={t("aia.l3")} value={totals.contractSumToDate} />
            <AiaLine n="4" label={t("aia.l4", { pct: Math.round(percentComplete) })} value={totals.totalCompletedToDate} />
            <AiaLine n="5" label={t("aia.l5")} value={totals.retainageAmount}>
              <div className="mt-2 space-y-1 pl-4">
                <SubLine label={t("aia.l5a", { pct: retainagePct })} value={totals.retainageCompleted} />
                <SubLine label={t("aia.l5b", { pct: retainagePct })} value={totals.retainageStored} />
              </div>
            </AiaLine>
            <AiaLine n="6" label={t("aia.l6")} value={totals.totalEarnedLessRetainage} />
            <AiaLine n="7" label={t("aia.l7")} value={totals.previousCerts} />
            <AiaLine n="8" label={t("aia.l8")} value={totals.currentPaymentDue} emphasis />
            <AiaLine n="9" label={t("aia.l9")} value={totals.balanceToFinish} />
          </dl>
        </div>
      </article>

      {/* Change orders / certification / notary / architect's certificate —
          a fixed, always-the-same-length tail (unlike the G703 continuation
          below, none of this varies with the number of SOV line items), so
          rather than let it fall wherever natural pagination lands (which
          Chrome and Safari resolve inconsistently — see the print CSS notes
          on box-decoration-break and .sd-g702-parties for two other cases
          of the same disagreement), it's a second, always-separate sheet
          with its own guaranteed page-top margin. This is the same
          treatment the G703 continuation sheet already gets below, which
          has been reliably correct in every real print test in both
          browsers all along. */}
      <article className="sd-sheet sd-sheet-page2 sd-card mt-5 p-6 lg:p-10">
        <div className="sd-numeric flex items-baseline justify-between gap-4 border-b border-border pb-4 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          <span>
            {t("aia.docTitle")} · {doc.docId.toUpperCase()} · {t("aia.appNo", { no: appNo })}
          </span>
          <span>{t("aia.continued")}</span>
        </div>

        {/* Change order recap — required by the form */}
        <div className="sd-block sd-g702-co-recap grid gap-4 border-b border-border py-5 sm:grid-cols-3">
          <Field label={t("aia.co.additions")}>
            <span className="sd-numeric text-sm">{money(Math.max(0, totals.changeOrders))}</span>
          </Field>
          <Field label={t("aia.co.deductions")}>
            <span className="sd-numeric text-sm">{money(Math.max(0, -totals.changeOrders))}</span>
          </Field>
          <Field label={t("aia.co.net")}>
            <span className="sd-numeric text-sm font-semibold">{money(totals.changeOrders)}</span>
          </Field>
        </div>

        {/* Certification + notary */}
        <div className="sd-g702-cert-notary grid gap-8 py-6 lg:grid-cols-2">
          <div className="sd-block">
            <div className="sd-label">{t("aia.cert.title")}</div>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t("aia.cert.body")}</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <SignatureLine label={t("aia.cert.contractor")} />
              <SignatureLine label={t("aia.cert.date")} />
            </div>
          </div>
          <div className="sd-block">
            <div className="sd-label">{t("aia.notary.title")}</div>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <SignatureLine label={t("aia.notary.state")} />
              <SignatureLine label={t("aia.notary.county")} />
            </div>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{t("aia.notary.body")}</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <SignatureLine label={t("aia.notary.public")} />
              <SignatureLine label={t("aia.notary.expires")} />
            </div>
          </div>
        </div>

        {/* Architect's certificate */}
        <div className="sd-block border-t border-border pt-6">
          <div className="sd-label">{t("aia.arch.title")}</div>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t("aia.arch.body")}</p>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <div className="sd-numeric">
              <div className="sd-label">{t("aia.arch.amountCertified")}</div>
              <div className="mt-2 text-2xl font-bold text-arc">{money(totals.currentPaymentDue)}</div>
            </div>
            <div className="grid flex-1 gap-4 sm:grid-cols-2">
              <SignatureLine label={t("aia.arch.architect")} />
              <SignatureLine label={t("aia.cert.date")} />
            </div>
          </div>
        </div>

        <footer className="mt-8 border-t border-border pt-5 font-mono text-[10px] uppercase tracking-[0.16em] text-arc">
          {t("aia.footer")}
        </footer>
      </article>

      <G703Continuation doc={doc} appNo={appNo} rows={rows.map((r) => r.calc)} descriptions={doc.items.map((i) => i.desc)} />
    </>
  );
}

function G703Continuation({
  doc,
  appNo,
  rows,
  descriptions,
}: {
  doc: G702Application;
  appNo: number;
  rows: ReturnType<typeof lineCalc>[];
  descriptions: string[];
}) {
  const t = useT();
  const sum = (pick: (r: ReturnType<typeof lineCalc>) => number) => rows.reduce((a, r) => a + pick(r), 0);

  return (
    <article className="sd-sheet sd-sheet-continuation sd-card mt-5 p-6 lg:p-10">
      <header className="flex flex-wrap items-start justify-between gap-6 border-b border-border pb-6">
        <div>
          <div className="sd-label">{t("aia.g703.form")}</div>
          <h2 className="sd-aia-title mt-2 font-display text-2xl uppercase tracking-wide lg:text-3xl">{t("aia.g703.title")}</h2>
          <div className="sd-numeric mt-3 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            {doc.projectName || doc.docId} · {t("aia.appNo", { no: appNo })}
          </div>
        </div>
        <div className="sd-numeric text-right text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          {doc.docId.toUpperCase()} · {t("docs.view.rev", { rev: doc.revision })}
        </div>
      </header>

      <div className="mt-6 overflow-x-auto print:overflow-visible">
        <table className="w-full min-w-[820px] border-collapse text-sm print:min-w-0 print:table-fixed">
          <thead>
            <tr className="border-b border-border text-left">
              <Th>{t("aia.g703.colA")}</Th>
              <Th>{t("aia.g703.colB")}</Th>
              <Th right>{t("aia.g703.colC")}</Th>
              <Th right>{t("aia.g703.colD")}</Th>
              <Th right>{t("aia.g703.colE")}</Th>
              <Th right>{t("aia.g703.colF")}</Th>
              <Th right>{t("aia.g703.colG")}</Th>
              <Th right>{t("aia.g703.colPct")}</Th>
              <Th right>{t("aia.g703.colH")}</Th>
              <Th right>{t("aia.g703.colI")}</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r, i) => (
              <tr key={i}>
                <Td className="sd-numeric text-muted-foreground">{String(i + 1).padStart(2, "0")}</Td>
                <Td className="font-semibold">{descriptions[i]}</Td>
                <Td right>{money(r.scheduled)}</Td>
                <Td right>{money(r.prev)}</Td>
                <Td right>{money(r.thisPeriod)}</Td>
                <Td right>{money(r.stored)}</Td>
                <Td right className="font-semibold">
                  {money(r.totalToDate)}
                </Td>
                <Td right>{Math.round(r.pct)}%</Td>
                <Td right>{money(r.balance)}</Td>
                <Td right>{money(r.retainage)}</Td>
              </tr>
            ))}
          </tbody>
          {/* A second <tbody>, not <tfoot> — deliberately. <thead>/<tfoot>
              content repeats on every printed page once a table's body
              spans more than one physical page (the same browser behavior
              that correctly repeats the column headers above), which is
              wanted for the header but not for a one-time totals row. Found
              live on the 30-line sample document (backlog PRINT-4): the
              totals row printed twice — once at the row-20 page break and
              again at the true end — both showing the identical final sum,
              reading as a false subtotal boundary. A second <tbody> never
              triggers that repeat, with identical visual output. */}
          <tbody>
            <tr className="border-t border-border-strong">
              <Td />
              <Td className="sd-label">{t("aia.g703.totals")}</Td>
              <Td right strong>
                {money(sum((r) => r.scheduled))}
              </Td>
              <Td right strong>
                {money(sum((r) => r.prev))}
              </Td>
              <Td right strong>
                {money(sum((r) => r.thisPeriod))}
              </Td>
              <Td right strong>
                {money(sum((r) => r.stored))}
              </Td>
              <Td right strong>
                {money(sum((r) => r.totalToDate))}
              </Td>
              <Td right />
              <Td right strong>
                {money(sum((r) => r.balance))}
              </Td>
              <Td right strong>
                {money(sum((r) => r.retainage))}
              </Td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="sd-g703-footnote mt-4 text-xs text-muted-foreground">{t("aia.g703.footer")}</p>

      <footer className="mt-8 border-t border-border pt-5 font-mono text-[10px] uppercase tracking-[0.16em] text-arc">
        {t("aia.footer")}
      </footer>
    </article>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="sd-label">{label}</div>
      <div className="mt-2 break-words">{children}</div>
    </div>
  );
}

function AiaLine({
  n,
  label,
  value,
  children,
  emphasis,
  signed,
}: {
  n: string;
  label: string;
  value: number;
  children?: ReactNode;
  emphasis?: boolean;
  signed?: boolean;
}) {
  return (
    <div className="py-3">
      <div className="flex items-baseline justify-between gap-4">
        <dt className="flex min-w-0 items-baseline gap-3">
          <span className="sd-numeric text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{n}</span>
          <span className={cn("text-sm", emphasis && "font-semibold")}>{label}</span>
        </dt>
        <dd
          className={cn(
            "sd-numeric shrink-0 text-sm font-semibold",
            emphasis && "text-2xl font-bold text-arc",
          )}
        >
          {signed && value > 0 ? "+" : ""}
          {money(value)}
        </dd>
      </div>
      {children}
    </div>
  );
}

function SubLine({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-[13px] text-muted-foreground">{label}</span>
      <span className="sd-numeric text-[13px] text-muted-foreground">{money(value)}</span>
    </div>
  );
}

function SignatureLine({ label, value }: { label: string; value?: string }) {
  return (
    <div>
      <div className="sd-numeric border-b border-border-strong pb-1 text-sm">{value ?? " "}</div>
      <div className="sd-label mt-1">{label}</div>
    </div>
  );
}

/** Every G703 column label follows "{letter} · {name}" (e.g. "C · Sched.
 *  value") except the bare "%" column (the AIA form itself never assigns
 *  that column a letter) — split on screen for a near no-op (the spans
 *  render inline, same as plain text, just with the bare letter
 *  parenthesized — "(C) · Sched. value" — matching how a real printed AIA
 *  G703 labels its columns), but print stacks the letter above the name
 *  as two separate right-aligned lines (see .sd-g703-th-* in
 *  tailwind-preview.css). On the narrow printed column width,
 *  "{letter} · {name}" all on one line was both truncating and, since it
 *  started well left of the right-aligned number underneath, hard to
 *  visually match to its column. The letter span always renders — a
 *  non-breaking space when there is no letter (e.g. "%") — purely so
 *  print can reserve its line's height. A genuinely EMPTY span generates
 *  no line box at all in some browsers, which left "%"'s single line
 *  vertically centered across the full 2-line cell instead of sitting on
 *  the second line where its neighbors' names sit — the nbsp forces a
 *  real (if invisible) line, so vertical-align: middle centers the same
 *  2-line box height everywhere. */
function Th({ children, right }: { children?: ReactNode; right?: boolean }) {
  const parts = typeof children === "string" ? children.split(" · ") : null;
  const hasLetter = parts !== null && parts.length === 2;
  const label = hasLetter ? parts[1] : children;
  return (
    <th className={cn("sd-label whitespace-nowrap px-2 pb-3", right && "text-right")}>
      <span className="sd-g703-th-letter">{hasLetter ? `(${parts[0]})` : " "}</span>
      {hasLetter ? <span className="sd-g703-th-sep"> · </span> : null}
      <span className="sd-g703-th-label">{label}</span>
    </th>
  );
}

function Td({
  children,
  right,
  strong,
  className,
}: {
  children?: ReactNode;
  right?: boolean;
  strong?: boolean;
  className?: string;
}) {
  return (
    <td
      className={cn(
        "px-2 py-3 align-top",
        right && "sd-numeric whitespace-nowrap text-right",
        strong && "font-semibold",
        className,
      )}
    >
      {children}
    </td>
  );
}
